import {pluralize} from 'BenchlingUtil';
import {isSubType, isLeafTypePrimitive} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {getTypeDefFromSchemaOrItemType} from 'data-types/benchling-type-def/ModelToTypeDef';
import {readableTypeDef} from 'data-types/benchling-type-def/readableTypeDef';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {
  SchemaField,
  FIELD_TYPE_TO_LEAF_TYPE,
  getRequiredLinkFolderItemType,
  getRequiredLinkSchemaId,
} from 'schemas/fields/SchemaFieldConstants';
import {isSchemaInterface, isTagSchemaWithInterfaces} from 'schemas/SchemaPredicates';

export default function getLookupConfigValidationMessage(
  inferredType: InferredLookupConfigType,
  toSchemaField: SchemaField,
  allSchemasById: SchemaById,
  selectorsById: Record<string, {name: string}>
): string | null {
  const fieldTypeDef = FIELD_TYPE_TO_LEAF_TYPE[toSchemaField.fieldType];
  if (!isSubType(inferredType.typeDef, fieldTypeDef)) {
    return `${toSchemaField.name} expects type ${readableTypeDef(fieldTypeDef)} but got ${readableTypeDef(
      inferredType.typeDef
    )}.`;
  }

  const isPrimitive = isLeafTypePrimitive(fieldTypeDef);
  if (inferredType.isMultiplex && (isPrimitive || !toSchemaField.isMulti)) {
    return `${toSchemaField.name} expects exactly one value but the mapper can return multiple values.`;
  }

  if (isPrimitive) {
    return null;
  }

  const requiredLinkFolderItemType = getRequiredLinkFolderItemType(
    toSchemaField
  ) as TagSchemaSupportedFolderItemType | null;
  const requiredLinkSchemaId = getRequiredLinkSchemaId(toSchemaField);
  const requiredLinkSchema =
    requiredLinkSchemaId != null ? (allSchemasById[requiredLinkSchemaId] ?? null) : null;
  const exactRequiredTypeDef = getTypeDefFromSchemaOrItemType(
    requiredLinkSchema,
    requiredLinkFolderItemType,
    fieldTypeDef
  );
  if (!isSubType(inferredType.typeDef, exactRequiredTypeDef)) {
    return `${toSchemaField.name} expects type ${readableTypeDef(
      exactRequiredTypeDef
    )} but got ${readableTypeDef(inferredType.typeDef)}.`;
  }

  if (requiredLinkSchema != null && inferredType.linkSchema?.id !== requiredLinkSchema.id) {
    if (isSchemaInterface(requiredLinkSchema) && inferredType.linkSchema) {
      const inferredLinkSchema = allSchemasById[inferredType.linkSchema.id];

      if (
        !(
          inferredLinkSchema &&
          isTagSchemaWithInterfaces(inferredLinkSchema) &&
          inferredLinkSchema.interfaces.some(
            (schemaInterface) => schemaInterface.id === requiredLinkSchema.id
          )
        )
      ) {
        return `${toSchemaField.name} expects ${pluralize(
          toSchemaField.isMulti ? 2 : 1,
          readableTypeDef(exactRequiredTypeDef)
        )} of fieldset ${requiredLinkSchema.name} but got items of schema ${
          inferredType.linkSchema?.name ?? 'None'
        } which do not apply that fieldset.`;
      }
    } else {
      return `${toSchemaField.name} expects ${pluralize(
        toSchemaField.isMulti ? 2 : 1,
        readableTypeDef(exactRequiredTypeDef)
      )} of schema ${requiredLinkSchema.name} but got items of schema ${
        inferredType.linkSchema?.name ?? 'None'
      }.`;
    }
  }

  const requiredSchemaFieldSelectorId = toSchemaField.schemaFieldSelectorId;
  if (
    requiredSchemaFieldSelectorId != null &&
    requiredSchemaFieldSelectorId !== inferredType.linkSelectorId
  ) {
    const requiredSchemaFieldSelectorDisplayName =
      selectorsById[requiredSchemaFieldSelectorId]?.name ?? requiredSchemaFieldSelectorId;
    let inferredSchemaFieldSelectorDisplayName;
    if (inferredType.linkSelectorId == null) {
      inferredSchemaFieldSelectorDisplayName = 'None';
    } else {
      inferredSchemaFieldSelectorDisplayName =
        selectorsById[inferredType.linkSelectorId]?.name ?? inferredType.linkSelectorId;
    }
    return `${toSchemaField.name} expects options of dropdown '${requiredSchemaFieldSelectorDisplayName}' but got '${
      inferredSchemaFieldSelectorDisplayName
    }'.`;
  }

  return null;
}
